import React from 'react';
import { useLocation } from 'react-router-dom';
import { GameStateDB } from '../../../data/datatypes';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';
import { useAutoSaveOnPageLeave } from '../../../store/hooks';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import PrimaryButton from '../../atoms/PrimaryButton';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import { GameCheckFeedbackData, GameCheckFeedbackState } from '../Games.types';
import ToggleSwitch from './ToggleSwitch';
import checkMarkIcon from '../../../static/icons/icon_check-mark_green.svg';
import dismissIcon from '../../../static/icons/icon_dismiss_red.svg';

type Properties = {
    data: GameCheckFeedbackData;
    setNextButtonLabel: (label: string | undefined) => void;
};

const CheckFeedback = (props: Properties) => {
    const { caption, desc, answers } = props.data;

    const path = useLocation().pathname;
    const dbId: `game_${string}` = `game_${getUrlWithTransientStateProperties(path)}`;

    const [step, setStep] = React.useState<'playing' | 'result'>('playing');
    const [currentInput, setCurrentInput] = React.useState<(boolean | null)[]>(new Array(answers.length).fill(null));
    const [inputHistory, setInputHistory] = React.useState<{
        inputs: (boolean | null)[];
        time: string;
    }[]>([]);

    // reset internal state when the data changes (fix for displaying the game directly after each other)
    React.useEffect(() => {
        setStep('playing');
        setCurrentInput(new Array(answers.length).fill(null));
        setInputHistory([]);
    }, [props.data]);

    useAutoSaveOnPageLeave(
        [step, currentInput, inputHistory],
        async () => {
            const gameState: GameStateDB = {
                _id: dbId,
                state: {
                    step,
                    currentInput,
                    inputHistory
                }
            };
            await saveGameState(gameState);
        },
        [path],
        async () => {
            const state = await getGameState(dbId);
            if (state) {
                setStep((state.state as GameCheckFeedbackState).step);
                setCurrentInput((state.state as GameCheckFeedbackState).currentInput);
                setInputHistory((state.state as GameCheckFeedbackState).inputHistory);
            }
        }
    );

    // set next-Button label
    React.useEffect(() => {
        props.setNextButtonLabel('Spiel beenden');
        return () => {
            props.setNextButtonLabel(undefined);
        };
    }, [props.setNextButtonLabel]);

    const showResult = () => {
        const newHistoryEntry = {
            inputs: currentInput,
            time: new Date().toJSON()
        };
        setInputHistory([newHistoryEntry, ...inputHistory]);
        setStep('result');
    };

    const restart = () => {
        setCurrentInput(new Array(answers.length).fill(null));
        setStep('playing');
    };

    const answerComponents = answers.map((answer, index) => {
        const i = index;
        const toggleClicked = (value: boolean | null) => {
            let newCurrentInput = [...currentInput];
            newCurrentInput[i] = value;

            if (currentInput.length > answers.length) {
                newCurrentInput = newCurrentInput.slice(0, answers.length);
            }

            setCurrentInput(newCurrentInput);
        };

        const answerCorrect = answer.correct === currentInput[i];

        let explanation = <></>;
        let checkmark = <></>;
        if (step === 'result') {
            if (answerCorrect) {
                if (answer.rightHint !== undefined) {
                    explanation = <div className="answer-explanation">
                        <TextWithReferences text={answer.rightHint} />
                    </div>;
                }
                checkmark = <div className="answer-checkmark">
                    <img
                        src={checkMarkIcon}
                        className="correct"
                    />
                </div>;
            } else {
                explanation = <div className="answer-explanation">
                    <TextWithReferences text={answer.wrongHint} />
                </div>;
                checkmark = <div className="answer-checkmark">
                    <img
                        src={dismissIcon}
                        className="incorrect"
                    />
                </div>;
            }
        }

        return <div
            key={index}
            className="answer"
        >
            <hr/>
            <div className="answer-line">
                <div className="answer-text">
                    {answer.answer}
                </div>
                {checkmark}
                <div className="answer-toggle">
                    <ToggleSwitch
                        value={currentInput[index]}
                        setValue={toggleClicked}
                        falseLabel="nein"
                        trueLabel="ja"
                        disabled={step === 'result'}
                    />
                </div>
            </div>
            {explanation}
        </div>;
    });

    let menuButton: JSX.Element;
    if (step === 'playing') {
        menuButton = <PrimaryButton
            label="Ergebnis anzeigen"
            onClick={showResult}
            disabled={currentInput.some(value => value === null)}
        />;
    } else {
        menuButton = <PrimaryButton
            label="Neu starten"
            onClick={restart}
        />;
    }

    return <div className="game">
        <div className="game-content check-feedback">
            <h1>{caption}</h1>
            <p><TextWithReferences text={desc || ''} /></p>
            {answerComponents}
        </div>
        <div className="game-menu">
            {menuButton}
        </div>
    </div>;
};

export default CheckFeedback;
